import { PDFDocument, StandardFonts, rgb, type PDFPage } from "pdf-lib";
import type {
  GA4ReportData,
  AgencyBrandConfig,
  GA4DailyRow,
} from "@agencylens/shared";
import {
  MARGIN_X,
  PAGE_H,
  PAGE_W,
  BODY,
  drawHeader,
  drawFooter,
  drawKpiGrid,
  drawTable,
  drawFallbackLineChart,
  fmtDateRange,
  fmtNumber,
  fmtPercent,
  type PDFFonts,
  type TableColumn,
} from "./render-utils.js";

export interface RenderGA4Options {
  data: GA4ReportData;
  agency: AgencyBrandConfig;
  /** Optional pre-rasterized chart PNG. If omitted, a pdf-lib fallback chart is drawn. */
  chartPng?: Uint8Array;
}

/* --------------------------------------------------------- summary stats */

function drawSummaryPage(
  page: PDFPage,
  fonts: PDFFonts,
  data: GA4ReportData,
  brand: AgencyBrandConfig,
): void {
  drawHeader(page, fonts, brand);

  let y = PAGE_H - 120;
  page.drawText(data.property.displayName, {
    x: MARGIN_X,
    y,
    size: 14,
    font: fonts.bold,
    color: rgb(0.2, 0.2, 0.2),
  });
  y -= 18;
  page.drawText(fmtDateRange(data.startDate, data.endDate), {
    x: MARGIN_X,
    y,
    size: 10,
    font: fonts.regular,
    color: rgb(0.45, 0.45, 0.45),
  });
  y -= 32;

  drawKpiGrid(
    page,
    fonts,
    [
      { label: "Sessions", value: fmtNumber(data.totals.sessions) },
      { label: "Users", value: fmtNumber(data.totals.totalUsers) },
      { label: "Engaged sessions", value: fmtNumber(data.totals.engagedSessions) },
      { label: "Bounce rate", value: fmtPercent(data.totals.bounceRate) },
      { label: "Conversions", value: fmtNumber(data.totals.conversions) },
      {
        label: "Engagement rate",
        value:
          data.totals.sessions > 0
            ? fmtPercent(data.totals.engagedSessions / data.totals.sessions)
            : "—",
      },
    ],
    y,
  );
}

/* ------------------------------------------------------- chart page */

async function drawChartPage(
  doc: PDFDocument,
  page: PDFPage,
  fonts: PDFFonts,
  data: GA4ReportData,
  brand: AgencyBrandConfig,
  chartPng?: Uint8Array,
): Promise<void> {
  drawHeader(page, fonts, brand);
  page.drawText("Sessions over time", {
    x: MARGIN_X,
    y: PAGE_H - 120,
    size: 14,
    font: fonts.bold,
    color: rgb(0.2, 0.2, 0.2),
  });

  if (chartPng) {
    const png = await doc.embedPng(chartPng);
    const maxW = PAGE_W - 2 * MARGIN_X;
    const maxH = BODY - 160;
    const aspect = png.width / png.height;
    let w = maxW;
    let h = w / aspect;
    if (h > maxH) {
      h = maxH;
      w = h * aspect;
    }
    page.drawImage(png, {
      x: MARGIN_X + (maxW - w) / 2,
      y: PAGE_H - 140 - h,
      width: w,
      height: h,
    });
    return;
  }

  drawFallbackLineChart(
    page,
    fonts,
    brand,
    data.daily.map((d) => ({ date: d.date, value: d.sessions })),
    { topY: PAGE_H - 150 },
  );
}

/* ---------------------------------------------------- table page(s) */

const GA4_COLUMNS: TableColumn<GA4DailyRow>[] = [
  { label: "Date", width: 80, format: (r) => r.date },
  { label: "Sessions", width: 80, align: "right", format: (r) => fmtNumber(r.sessions) },
  { label: "Users", width: 80, align: "right", format: (r) => fmtNumber(r.totalUsers) },
  { label: "Engaged", width: 80, align: "right", format: (r) => fmtNumber(r.engagedSessions) },
  { label: "Bounce", width: 70, align: "right", format: (r) => fmtPercent(r.bounceRate) },
  { label: "Conv.", width: 70, align: "right", format: (r) => fmtNumber(r.conversions) },
];

function drawTablePage(
  page: PDFPage,
  fonts: PDFFonts,
  rows: GA4DailyRow[],
  brand: AgencyBrandConfig,
  pageLabel: string,
): void {
  drawHeader(page, fonts, brand);
  page.drawText(pageLabel, {
    x: MARGIN_X,
    y: PAGE_H - 120,
    size: 14,
    font: fonts.bold,
    color: rgb(0.2, 0.2, 0.2),
  });
  drawTable(page, fonts, brand, GA4_COLUMNS, rows, PAGE_H - 150);
}

/* --------------------------------------------------------- main render */

const ROWS_PER_TABLE_PAGE = 31;

/**
 * Render a GA4 PDF report.
 *
 * Layout:
 *   Page 1 — header + summary stat cards
 *   Page 2 — sessions-over-time chart (PNG if provided, fallback line if not)
 *   Page 3+ — daily breakdown table (paginated)
 */
export async function renderGA4Report(
  opts: RenderGA4Options,
): Promise<Uint8Array> {
  const { data, agency, chartPng } = opts;
  const doc = await PDFDocument.create();
  doc.setTitle(`${agency.agencyName} — ${data.property.displayName}`);
  doc.setAuthor(agency.agencyName);
  doc.setProducer("AgencyLens");
  doc.setCreator("AgencyLens");
  doc.setCreationDate(new Date());

  const regular = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  const fonts: PDFFonts = { regular, bold };

  // Page 1 — summary
  const summary = doc.addPage([PAGE_W, PAGE_H]);
  drawSummaryPage(summary, fonts, data, agency);

  // Page 2 — chart
  const chartPage = doc.addPage([PAGE_W, PAGE_H]);
  await drawChartPage(doc, chartPage, fonts, data, agency, chartPng);

  // Page 3+ — table
  const chunks: GA4DailyRow[][] = [];
  for (let i = 0; i < data.daily.length; i += ROWS_PER_TABLE_PAGE) {
    chunks.push(data.daily.slice(i, i + ROWS_PER_TABLE_PAGE));
  }
  if (chunks.length === 0) chunks.push([]); // empty page if no rows

  chunks.forEach((chunk, idx) => {
    const page = doc.addPage([PAGE_W, PAGE_H]);
    const label =
      chunks.length === 1
        ? "Daily breakdown"
        : `Daily breakdown (${idx + 1} of ${chunks.length})`;
    drawTablePage(page, fonts, chunk, agency, label);
  });

  // Footers (after we know the total page count)
  const pages = doc.getPages();
  const total = pages.length;
  pages.forEach((p, i) => drawFooter(p, fonts, agency, i + 1, total));

  return doc.save();
}
